import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, Callout, Spinner, TextField, Select, Modal, Badge, Gauge, CopyField } from "@/design/components";
import { ApiError } from "@/api/client";
import {
  listMailAccounts,
  createMailAccount,
  setMailQuota,
  rotateMailPassword,
  setMailStatus,
  deleteMailAccount,
  isMailUnavailable,
  type MailAccount,
  type MailAccountStatus,
} from "@/api/mail";
import { listChannels } from "@/api/channels";
import styles from "./MailScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatBytes(n: number): string {
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(n % 1_073_741_824 === 0 ? 0 : 1)} GB`;
  if (n >= 1_048_576) return `${Math.round(n / 1_048_576)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

// Quota presets offered in the UI (bytes) — a data change adds a size, not new UI.
const QUOTA_OPTIONS = [
  { value: "536870912", label: "512 MB" },
  { value: "1073741824", label: "1 GB" },
  { value: "2147483648", label: "2 GB" },
  { value: "5368709120", label: "5 GB" },
];

const STATUS_TONE: Record<MailAccountStatus, "success" | "neutral"> = { active: "success", suspended: "neutral" };
const STATUS_LABEL: Record<MailAccountStatus, string> = { active: "Active", suspended: "Suspended" };

/** A generated password to show the operator exactly once (create + rotate). */
interface Revealed {
  address: string;
  password: string;
  title: string;
}

/**
 * Mail — the admin surface for per-agent mailboxes. Each agent gets a real mailbox
 * (`[peer]-[channel]@mail.cogent.tools`) on the Cogent mail server; from here an admin
 * provisions one, watches its disk usage, changes its quota, rotates its password,
 * suspends/resumes it, or deletes it. Mirrors the Channels/Integrations screens.
 */
export function MailScreen() {
  const qc = useQueryClient();
  const mailboxesQuery = useQuery({ queryKey: ["mail-accounts"], queryFn: ({ signal }) => listMailAccounts(signal) });
  // The channel <Select> reuses the Channels data source (shared cache key).
  const channelsQuery = useQuery({ queryKey: ["channels"], queryFn: ({ signal }) => listChannels(signal) });
  const channelOptions = (channelsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [quotaFor, setQuotaFor] = useState<MailAccount | null>(null);
  const [toDelete, setToDelete] = useState<MailAccount | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["mail-accounts"] });

  const create = useMutation({
    mutationFn: (input: { peerId: string; channelId?: string }) => createMailAccount(input),
    onSuccess: (m) => {
      invalidate();
      setCreateOpen(false);
      setRevealed({ address: m.address, password: m.password, title: "Mailbox created" });
    },
  });

  const rotate = useMutation({
    mutationFn: (m: MailAccount) => rotateMailPassword(m.id).then((r) => ({ ...r, address: m.address })),
    onSuccess: (r) => {
      invalidate();
      setRevealed({ address: r.address, password: r.password, title: "Password rotated" });
    },
  });

  const quota = useMutation({
    mutationFn: (v: { id: string; quotaBytes: number }) => setMailQuota(v.id, v.quotaBytes),
    onSuccess: () => {
      invalidate();
      setQuotaFor(null);
    },
  });

  const status = useMutation({
    mutationFn: (v: { id: string; status: MailAccountStatus }) => setMailStatus(v.id, v.status),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMailAccount(id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
    },
  });

  const { data: mailboxes, isPending, isError, error } = mailboxesQuery;
  const unavailable = isError && isMailUnavailable(error);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Mail</h1>
        </div>
        {!isPending && !isError && (
          <div className={styles.headActions}>
            <Button onClick={() => { create.reset(); setCreateOpen(true); }} iconBefore={<PlusIcon />}>
              New mailbox
            </Button>
          </div>
        )}
      </header>

      {isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading mailboxes…</span>
        </div>
      )}

      {unavailable && (
        <Callout tone="warning" title="Mail isn't enabled yet">
          The control plane isn't linked to a Cogent mail server, so mailboxes can't be managed from here
          yet. Once the mail box is configured, your agents' mailboxes will appear here.
        </Callout>
      )}

      {isError && !unavailable && (
        <Callout tone="danger" title="Couldn't load mailboxes">
          {apiMessage(error, "Please try again shortly.")}
        </Callout>
      )}

      {mailboxes && mailboxes.length === 0 && (
        <Card className={styles.empty}>
          <h2 className={styles.emptyTitle}>No mailboxes yet</h2>
          <p className={styles.emptyBlurb}>
            Give an agent a real mailbox on <code>mail.cogent.tools</code> — it can send and receive email
            (with attachments) and keeps a durable, offline-friendly inbox you can read with any mail client.
          </p>
          <Button onClick={() => { create.reset(); setCreateOpen(true); }} iconBefore={<PlusIcon />}>
            Create your first mailbox
          </Button>
        </Card>
      )}

      {mailboxes && mailboxes.length > 0 && (
        <ul className={styles.list}>
          {mailboxes.map((m) => (
            <li key={m.id}>
              <Card className={styles.row}>
                <div className={styles.rowMain}>
                  <h2 className={styles.name}>{m.address}</h2>
                  <div className={styles.status}>
                    <Badge tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Badge>
                    {m.channelName && <span className={styles.hint}>{m.channelName}</span>}
                  </div>
                  <div className={styles.gauge}>
                    <Gauge label="Storage" used={m.usedBytes} limit={m.quotaBytes} unit="bytes" />
                    <p className={styles.meta}>
                      Quota {formatBytes(m.quotaBytes)}
                      {m.messageCount != null ? ` · ${m.messageCount} ${m.messageCount === 1 ? "message" : "messages"}` : ""}
                      {m.lastActivityAt ? ` · Last activity ${formatDate(m.lastActivityAt)}` : ""}
                      {` · Created ${formatDate(m.createdAt)}`}
                      {m.rotatedAt ? ` · Rotated ${formatDate(m.rotatedAt)}` : ""}
                    </p>
                  </div>
                </div>
                <div className={styles.rowActions}>
                  <Button variant="secondary" size="sm" onClick={() => { quota.reset(); setQuotaFor(m); }}>
                    Quota
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={rotate.isPending && rotate.variables?.id === m.id}
                    onClick={() => rotate.mutate(m)}
                  >
                    Rotate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={status.isPending && status.variables?.id === m.id}
                    onClick={() => status.mutate({ id: m.id, status: m.status === "active" ? "suspended" : "active" })}
                  >
                    {m.status === "active" ? "Suspend" : "Resume"}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => { remove.reset(); setToDelete(m); }}>
                    Delete
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {rotate.isError && (
        <div role="alert">
          <Callout tone="danger">{apiMessage(rotate.error, "Couldn't rotate the password.")}</Callout>
        </div>
      )}
      {status.isError && (
        <div role="alert">
          <Callout tone="danger">{apiMessage(status.error, "Couldn't change the mailbox status.")}</Callout>
        </div>
      )}

      {/* Create mailbox */}
      <Modal open={createOpen} onClose={() => { if (!create.isPending) setCreateOpen(false); }} title="New mailbox">
        <CreateMailboxForm
          channelOptions={channelOptions}
          pending={create.isPending}
          error={create.isError ? apiMessage(create.error, "Couldn't create the mailbox.") : null}
          onSubmit={(values) => create.mutate(values)}
        />
      </Modal>

      {/* One-time password reveal (create + rotate) */}
      <Modal
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title={revealed?.title ?? "Mailbox password"}
        footer={<Button onClick={() => setRevealed(null)}>Done</Button>}
      >
        {revealed && (
          <div className={styles.reveal}>
            <Callout tone="warning" title="Copy this password now">
              This password is shown <strong>once</strong> and is never stored. Give it to the agent (or save
              it in the agent's credentials) — if you lose it, rotate the mailbox to get a new one.
            </Callout>
            <CopyField label="Address" value={revealed.address} />
            <CopyField label="Password" value={revealed.password} />
          </div>
        )}
      </Modal>

      {/* Quota edit */}
      <Modal
        open={quotaFor !== null}
        onClose={() => { if (!quota.isPending) setQuotaFor(null); }}
        title="Change quota"
      >
        {quotaFor && (
          <QuotaForm
            current={quotaFor.quotaBytes}
            pending={quota.isPending}
            error={quota.isError ? apiMessage(quota.error, "Couldn't change the quota.") : null}
            onCancel={() => { if (!quota.isPending) setQuotaFor(null); }}
            onSubmit={(quotaBytes) => quota.mutate({ id: quotaFor.id, quotaBytes })}
          />
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={toDelete !== null}
        onClose={() => { if (!remove.isPending) setToDelete(null); }}
        title="Delete mailbox?"
        footer={
          <>
            <Button variant="ghost" onClick={() => { if (!remove.isPending) setToDelete(null); }} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => toDelete && remove.mutate(toDelete.id)}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Delete <strong>{toDelete?.address}</strong>? The mailbox and everything in it are removed from the
          mail server. This can't be undone.
        </p>
        {remove.isError && <Callout tone="danger">{apiMessage(remove.error, "Couldn't delete the mailbox.")}</Callout>}
      </Modal>
    </div>
  );
}

// --- Create form (RHF + Zod) -----------------------------------------------------
const createMailboxSchema = z.object({
  peerId: z.string().min(1, "A peer id is required").max(128),
  channelId: z.string().optional(),
});
type CreateMailboxValues = z.infer<typeof createMailboxSchema>;

function CreateMailboxForm({
  channelOptions,
  pending,
  error,
  onSubmit,
}: {
  channelOptions: { value: string; label: string }[];
  pending: boolean;
  error: string | null;
  onSubmit: (values: { peerId: string; channelId?: string }) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateMailboxValues>({
    resolver: zodResolver(createMailboxSchema),
    defaultValues: { peerId: "", channelId: "" },
  });

  function submit(values: CreateMailboxValues) {
    onSubmit({ peerId: values.peerId.trim(), channelId: values.channelId?.trim() ? values.channelId.trim() : undefined });
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(submit)} noValidate>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <TextField
        label="Peer / agent id"
        autoComplete="off"
        hint="e.g. claude-agent — becomes the mailbox name"
        required
        error={errors.peerId?.message}
        {...register("peerId")}
      />
      <Select
        label="Channel (optional)"
        options={[{ value: "", label: "No channel" }, ...channelOptions]}
        hint="Bind the mailbox to a channel; its name is part of the address."
        error={errors.channelId?.message}
        {...register("channelId")}
      />
      <div className={styles.formActions}>
        <Button type="submit" loading={pending}>
          Create mailbox
        </Button>
      </div>
    </form>
  );
}

// --- Quota form ------------------------------------------------------------------
function QuotaForm({
  current,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  current: number;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (quotaBytes: number) => void;
}) {
  // Preselect the current quota if it matches a preset, else the first option.
  const initial = QUOTA_OPTIONS.find((o) => Number(o.value) === current)?.value ?? QUOTA_OPTIONS[0]!.value;
  const [value, setValue] = useState(initial);
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(Number(value));
      }}
      noValidate
    >
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Select
        label="Mailbox quota"
        options={QUOTA_OPTIONS}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className={styles.formActions}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" loading={pending}>
          Save quota
        </Button>
      </div>
    </form>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
